import { credentialBlock, ocppCredential, ocppUrl } from "../../lib/credentials";
import { useDeployment } from "../../lib/deployment";
import type { ProvisionedStation } from "../../lib/types";
import { Button } from "../ui/Button";
import { CopyButton, Copyable } from "../ui/Copy";
import { MicroLabel } from "../ui/Text";

/**
 * The one and only sight of a station's password.
 *
 * The server hashes it with argon2id and returns the plaintext exactly once; no
 * endpoint can produce it again and no support process can recover it. That makes
 * this panel load-bearing rather than decorative, so it is built to be hard to
 * dismiss by accident: it does not auto-hide, it does not disappear when the
 * fleet table refetches behind it, and closing it takes a deliberate click on a
 * button that says what closing means.
 *
 * The password is deliberately not selectable-by-triple-click alone — there is a
 * copy button — but it is plain text in the DOM, because an operator on a machine
 * without clipboard permission still has to be able to read it off the screen.
 *
 * ## Why the assembled lines are here rather than in a runbook
 *
 * What actually happens next is that whoever registered the station sends the
 * credential to whoever is standing at it. That used to mean transcribing a
 * WebSocket URL out of a wiki page, substituting the id into it twice, and pasting
 * a password beside it — four chances to be wrong, on the one message nobody can
 * re-derive later because the password is gone from this application the moment
 * this panel closes. So the panel writes both lines, in the shape the ops team
 * already uses, and *Copy both* puts them on the clipboard together.
 *
 * The host half comes from the serving deployment (`lib/deployment.ts`), never from
 * this browser's own address — see `lib/credentials.ts` on why guessing it is worse
 * than saying nothing.
 */

export interface StationCredentialProps {
  readonly station: ProvisionedStation;
  /** "Registered" reads wrong after a rotation, so the caller supplies the verb. */
  readonly heading: string;
  readonly onDismiss: () => void;
}

export function StationCredential({ station, heading, onDismiss }: StationCredentialProps) {
  const { ocppBaseUrl, isResolving, hasFailed } = useDeployment();

  const chargePointId = station.chargePoint.id;
  const password = station.password;

  const url = ocppUrl({ ocppBaseUrl, chargePointId, password });
  const credential = ocppCredential(chargePointId, password);

  return (
    <section
      // `alert` rather than `status`: this is the only chance to read the value,
      // so a screen reader must interrupt rather than queue it politely.
      role="alert"
      aria-labelledby="station-credential-heading"
      className="flex flex-col gap-4 border-l-2 border-signal bg-paper-raised px-4 py-4"
    >
      <div className="flex items-baseline justify-between gap-4">
        <h2
          id="station-credential-heading"
          className="m-0 text-micro font-semibold uppercase tracking-[0.11em] text-ink"
        >
          {heading}
        </h2>
        <Button variant="ghost" onClick={onDismiss}>
          I have saved it
        </Button>
      </div>

      <dl className="m-0 flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <MicroLabel>Charge point id</MicroLabel>
          <dd className="m-0 font-mono text-body text-ink">{chargePointId}</dd>
        </div>

        <div className="flex flex-col gap-1">
          <MicroLabel>Password</MicroLabel>
          <dd className="m-0 flex flex-wrap items-center gap-3">
            <code className="select-all bg-paper px-2 py-1 font-mono text-lg tracking-[0.08em] text-ink">
              {password}
            </code>
            <CopyButton value={password} what="Password" />
          </dd>
        </div>
      </dl>

      {/*
        The two lines to hand on, and the button that takes both at once. Separated
        from the fields above by a rule rather than a heading: this is the same
        credential in the form it gets sent in, not a second thing to save.

        **Two kinds of copy, deliberately.** Each field copies its bare value, because
        the next place it goes is a single input on a charger's configuration screen
        and a `OCPP URL: ` prefix pasted into one is a station that cannot connect.
        *Copy both* is the labelled pair, because the next place *that* goes is a
        message to a person.
      */}
      <div className="flex flex-col gap-3 border-t border-rule pt-4">
        {url === undefined ? (
          /*
            No configured origin, so there is no URL to print. Named rather than
            silently omitted, and named as the specific thing to fix — an operator
            who sees only the credential line has no way to tell a missing feature
            from a missing environment variable.

            While the request is still in flight this says nothing at all: telling
            someone to edit a deployment's configuration and then finding out a
            moment later that it was already fine is worse than a beat of no answer.
          */
          isResolving ? null : hasFailed ? (
            /*
              Asked and refused, which is not the same as unconfigured — a token
              without `read` gets a 403 here — so this does not tell anyone to edit an
              environment file that may already be correct.
            */
            <p className="m-0 max-w-prose text-body text-ink-muted">
              This deployment's public address could not be read, so the WebSocket URL cannot be
              assembled. The credential below is unaffected.
            </p>
          ) : (
            <p className="m-0 max-w-prose text-body text-ink-muted">
              This deployment has not been told its own public address, so the WebSocket URL cannot
              be shown. Set <span className="font-mono">CSMS_PUBLIC_BASE_URL</span> on the server to
              the origin a charger reaches it at, and this panel writes the full line.
            </p>
          )
        ) : (
          <Copyable
            label="OCPP URL"
            value={url}
            hint="The whole address, credentials included. The station id appears twice on purpose — the server checks the path and the Basic username against each other."
          />
        )}

        <Copyable
          label="OCPP Credential"
          value={credential}
          hint="Id and password as one token, for a charger whose configuration screen takes them as two fields."
        />

        {url === undefined ? null : (
          <div className="flex flex-wrap items-center gap-3">
            <CopyButton
              variant="primary"
              what="Both lines"
              value={credentialBlock({ ocppBaseUrl, chargePointId, password })}
            >
              Copy both
            </CopyButton>
            <span className="text-micro tracking-normal normal-case text-ink-faint">
              Both lines with their labels, ready to paste to whoever is at the charger. The two
              buttons above copy the bare values, which is what a charger's own fields want.
            </span>
          </div>
        )}
      </div>

      <p className="m-0 max-w-prose text-body text-ink-muted">
        Save this now. It is stored only as a hash, so nothing — including this panel, the API and
        the database — can show it again. If it is lost, rotate the credential from the station's
        page and configure the new one.
      </p>
    </section>
  );
}
